import classNames from 'classnames'
import { CSSProperties, FC, ForwardRefExoticComponent, ReactNode, RefAttributes, RefObject, forwardRef, useRef } from 'react'

import { Paper, PaperProps, Tooltip, TooltipProps } from '@mui/material'

import './GRTooltip.scss'

/**
 * Component for displaying a tooltip while hovering an element.
 */

type GRTooltipProps = {
  children?: ReactNode
  content: ReactNode
  hidden?: boolean
  className?: string
  severity?: 'success' | 'warning'
  styles?: CSSProperties
  popoverBorderColor?: string
  anchorRef?: RefObject<HTMLElement>
  tooltipProps?: Partial<Omit<TooltipProps, 'title' | 'children'>>
  usePointerPosition?: boolean
  disableInteractive?: boolean
  disableMaxWidth?: boolean
}

export const GRTooltip: FC<GRTooltipProps> = ({
  content,
  hidden = false,
  children,
  className,
  severity,
  styles,
  popoverBorderColor,
  anchorRef,
  tooltipProps,
  usePointerPosition,
  disableInteractive,
  disableMaxWidth,
}) => {
  const positionRef = useRef<{ x?: number; y?: number }>()

  const handleMouseMove = (event: React.MouseEvent) => {
    positionRef.current = { x: event.clientX, y: event.clientY }
  }

  const triggerClasses = classNames('GRTooltip', className)
  const tooltipClasses = classNames(disableMaxWidth ? 'Tooltip Tooltip--NoMaxWidth' : 'Tooltip', severity)
  // if anchorRef is given, use it to calculate position for the tooltip
  const customAnchorElement =
    anchorRef || usePointerPosition
      ? {
          anchorEl: {
            getBoundingClientRect: () => {
              const cursorX = positionRef.current?.x
              const cursorY = positionRef.current?.y

              if (anchorRef) {
                const anchorRect = anchorRef.current?.getBoundingClientRect()
                if (!anchorRect) {
                  // Anchor element is gone (e.g. unmounted mid-update); avoid producing a
                  // DOMRect with NaN coordinates that would jump the tooltip to the screen edge.
                  return new DOMRect(cursorX ?? 0, cursorY ?? 0, 16, 24)
                }
                // Anchor to the item's horizontal center rather than the live cursor X.
                // The LE calendar re-creates its item components when a data fetch resolves,
                // which resets positionRef to undefined under a stationary cursor (the browser
                // does not re-fire mouseenter). Using cursorX while loading and the anchor center
                // afterwards made the tooltip visibly shift left/right the moment data loaded.
                // Centering on the anchor keeps the tooltip put across that transition.
                // Center on the *visible* portion of the bar so very wide or partially scrolled
                // events keep the tooltip near the on-screen segment instead of an off-screen center.
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth
                const visibleLeft = Math.max(anchorRect.left, 0)
                const visibleRight = Math.min(anchorRect.right, viewportWidth)
                const x = visibleRight > visibleLeft ? (visibleLeft + visibleRight) / 2 : anchorRect.left + anchorRect.width / 2
                return new DOMRect(x, anchorRect.top, 16, 24)
              } else {
                return new DOMRect(cursorX ?? 0, cursorY ?? 0, 16, 24)
              }
            },
          },
        }
      : undefined

  return content && !hidden ? (
    <Tooltip
      title={content}
      components={{ Tooltip: TooltipPaper }}
      componentsProps={{ tooltip: { className: tooltipClasses, style: popoverBorderColor ? { borderColor: popoverBorderColor } : {} } }}
      PopperProps={customAnchorElement}
      disableInteractive={disableInteractive === false ? false : true}
      {...tooltipProps}
    >
      <span onMouseMove={handleMouseMove} onMouseEnter={handleMouseMove} style={styles} className={triggerClasses}>
        {children}
      </span>
    </Tooltip>
  ) : !content || hidden ? (
    <>{children}</>
  ) : null
}

const TooltipPaper: ForwardRefExoticComponent<Omit<PaperProps<'div', {}>, 'ref'> & RefAttributes<HTMLDivElement>> = forwardRef(
  ({ className, style, children }, ref) => {
    return (
      <Paper className={className} style={style} elevation={8} sx={{ p: 2, borderRadius: '20px' }} ref={ref}>
        {children}
      </Paper>
    )
  }
)
